import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NoteTypeEnum } from 'app/shared/components/note/note-type.enum';
import { NoteModel } from 'app/shared/components/note/note.model';
import { EntityValidationResult } from 'app/shared/models/entity-validation-result';
import { SmartSnackDataModel } from './smart-snack/smart-snack-data.model';
import { SmartSnackComponent } from './smart-snack/smart-snack.component';

@Injectable({
  providedIn: 'root',
})
export class NotifyService {
  private _data: SmartSnackDataModel | null = null;
  private _doNotDismissFlag = false;
  private _flag = false;
  private _snackBar = inject(MatSnackBar);

  /**
   * Display one or more messages immediately, only if the "flagUp" was not called.
   */
  public _displayMessagesNow(
    noteType: NoteTypeEnum,
    messageOrMessages: string | string[] | undefined,
    actionFunction: (() => void) | null = null,
    actionLabel: string | null = null,
    actionExternalLinkFlag = false,
  ): void {
    if (!this._flag) {
      this._pushMessages(noteType, messageOrMessages, actionFunction, actionLabel, true, true, actionExternalLinkFlag);
      this.displayMessages();
    }

    this.flagDown();
  }

  /**
   * Display an error message.
   */
  public buildErrorMessage(error: HttpErrorResponse): Array<string> {
    const errorMessage = new Array<string>();

    if (error && error.error) {
      const entityValidationResult: EntityValidationResult = error.error;
      if (entityValidationResult.validationResults && entityValidationResult.validationResults.length > 0) {
        entityValidationResult.validationResults.forEach((validationResult) => {
          if (validationResult.memberNames.length === 0) {
            errorMessage.push(validationResult.errorMessage);
          }
        });
      }
    }

    return errorMessage;
  }

  /**
   * Display first error message.
   */
  public buildErrorMessageSingle(error: HttpErrorResponse): string {
    return `${this.buildErrorMessage(error)}`;
  }

  /**
   * Close the snackBar (in case of api call).
   */
  public dismiss(): void {
    if (this._doNotDismissFlag) {
      this._doNotDismissFlag = false;
    } else {
      this._snackBar.dismiss();
    }
  }

  /**
   * Display one or more ERROR messages immediately, only if the "flagUp" was not called.
   */
  public displayErrorMessagesNow(
    messageOrMessages: string | string[],
    actionFunction: (() => void) | null = null,
    actionLabel: string | null = null,
    actionExternalLinkFlag = false,
  ): void {
    this._displayMessagesNow(NoteTypeEnum.Error, messageOrMessages, actionFunction, actionLabel, actionExternalLinkFlag);
  }

  /**
   * Display one or more INFORMATION messages immediately, only if the "flagUp" was not called.
   */
  public displayInformationMessagesNow(
    messageOrMessages: string | string[] | undefined,
    actionFunction: (() => void) | null = null,
    actionLabel: string | null = null,
  ): void {
    this._displayMessagesNow(NoteTypeEnum.Information, messageOrMessages, actionFunction, actionLabel);
  }

  /**
   * Display one or more messages already pushed by "PushMessages".
   */
  public displayMessages(): void {
    if (this._data) {
      const config: Record<string, unknown> = {};
      config.data = this._data;

      this._snackBar.openFromComponent(SmartSnackComponent, config);
      this._clear();
    }
  }

  /**
   * Display one or more WAITING messages immediately, only if the "flagUp" was not called.
   */
  public displayWaitingMessagesNow(
    messageOrMessages: string | string[],
    actionFunction: (() => void) | null = null,
    actionLabel: string | null = null,
  ): void {
    this._displayMessagesNow(NoteTypeEnum.Waiting, messageOrMessages, actionFunction, actionLabel);
  }

  /**
   * Display one or more WARNING messages immediately, only if the "flagUp" was not called.
   */
  public displayWarningMessagesNow(
    messageOrMessages: string | string[],
    actionFunction: (() => void) | null = null,
    actionLabel: string | null = null,
  ): void {
    this._displayMessagesNow(NoteTypeEnum.Warning, messageOrMessages, actionFunction, actionLabel);
  }

  public doNotDismissOnNextApiCall(): void {
    this._doNotDismissFlag = true;
  }

  /**
   * Reset default behavior. Any error will show up on next API call.
   */
  public flagDown(): void {
    this._flag = false;
  }

  /**
   * Do not notify on next API call.
   * The method is usually called to suppress the display of messages by the ApiInterceptor when error handling will be managed by the caller of this method.
   */
  public flagUp(): void {
    this._flag = true;
  }

  /**
   * Returns true if the error.error.errorName is equal to the errorName provided.
   */
  public hasErrorName(error: HttpErrorResponse, errorName: string): boolean {
    if (error) {
      const entityValidationResult: EntityValidationResult = error.error;
      if (entityValidationResult && entityValidationResult.errorName === errorName) {
        return true;
      }
    }

    return false;
  }

  /**
   * Push one or more ERROR message. Displayable by calling "DisplayMessages".
   */
  public pushErrorMessages(
    messageOrMessages: string | string[],
    actionFunction: (() => void) | null = null,
    actionLabel = 'Annuler',
    overwriteMessagesFlag = true,
    overwriteActionFlag = true,
  ): void {
    this._pushMessages(NoteTypeEnum.Error, messageOrMessages, actionFunction, actionLabel, overwriteMessagesFlag, overwriteActionFlag);
  }

  /**
   * Push one or more INFORMATION message. Displayable by calling "DisplayMessages".
   */
  public pushInformationMessages(
    messageOrMessages: string | string[],
    actionFunction: (() => void) | null = null,
    actionLabel = 'Annuler',
    overwriteMessagesFlag = true,
    overwriteActionFlag = true,
  ): void {
    this._pushMessages(
      NoteTypeEnum.Information,
      messageOrMessages,
      actionFunction,
      actionLabel,
      overwriteMessagesFlag,
      overwriteActionFlag,
    );
  }

  /**
   * Push one or more WARNING message. Displayable by calling "DisplayMessages".
   */
  public pushWarningMessages(
    messageOrMessages: string | string[],
    actionFunction: (() => void) | null = null,
    actionLabel: string | null = 'Annuler',
    overwriteMessagesFlag = true,
    overwriteActionFlag = true,
  ): void {
    this._pushMessages(NoteTypeEnum.Warning, messageOrMessages, actionFunction, actionLabel, overwriteMessagesFlag, overwriteActionFlag);
  }

  /**
   * Clear a message already pushed by "PushMessage".
   */
  private _clear(): void {
    this._data = null;
  }

  /**
   * Push one or more messages. Displayable by calling "DisplayMessages".
   */
  private _pushMessages(
    noteType: NoteTypeEnum,
    messageOrMessages: string | string[] | undefined,
    actionFunction: (() => void) | null = null,
    actionLabel: string | null = 'Annuler',
    overwriteMessagesFlag = true,
    overwriteActionFlag = true,
    actionExternalLinkFlag = false,
  ): void {
    let messages: string[];
    if (messageOrMessages instanceof Array) {
      messages = messageOrMessages;
    } else {
      messages = [messageOrMessages!];
    }

    const notes: NoteModel[] = [];

    messages.forEach((message) => {
      const note = new NoteModel();
      note.message = message;
      note.noteType = noteType;
      notes.push(note);
    });

    this._data = {
      notes: overwriteMessagesFlag ? notes : this._data!.notes.concat(notes),
      actionFunction: overwriteActionFlag ? actionFunction : this._data!.actionFunction,
      actionLabel: overwriteActionFlag ? actionLabel : this._data!.actionLabel,
      actionExternalLinkFlag: overwriteActionFlag ? actionExternalLinkFlag : this._data!.actionExternalLinkFlag,
    };
  }
}
